import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Compass, Sparkles } from 'lucide-react';

import { ouvrirUseCase } from '../../utils/openUseCase';

interface Suggestion {
  id: number;
  titre: string;
  raison: string;
  pertinence: 'forte' | 'moyenne' | 'faible';
}

interface Props {
  motif: string;
  suggestions: Suggestion[];
  /** Confie la demande à Claude, l'assistant général. */
  onGeneral?: () => void;
}

const TEINTES: Record<string, string> = {
  forte: 'bg-eg-valide/10 text-eg-valide border-eg-valide/30',
  moyenne: 'bg-eg-accent/10 text-eg-accent border-eg-accent/30',
  faible: 'bg-eg-papier text-eg-gris border-eg-bord',
};

/**
 * Renvoi vers l'assistant adapté, quand la demande ne relève pas de celui-ci.
 *
 * L'assistant constatait déjà l'écart et produisait quand même : quelqu'un qui
 * demandait une note d'une page recevait un scénario vidéo, après avoir répondu
 * à trois questions de précision. On s'arrête avant, et on propose le bon
 * assistant plutôt que de renvoyer l'agent au catalogue où il s'est trompé.
 */
const Reorientation: React.FC<Props> = ({ motif, suggestions, onGeneral }) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-eg-alerte/30 bg-eg-alerte/10 px-4 py-3">
        <div className="mb-1 flex items-center gap-2 text-[10px] uppercase
          tracking-[0.04em] text-eg-alerte">
          <Compass className="h-3.5 w-3.5" />
          Ce n'est pas le bon assistant
        </div>
        <p className="text-[13px] leading-relaxed text-eg-encre">{motif}</p>
      </div>

      {onGeneral && (
        <button
          type="button"
          onClick={onGeneral}
          className="flex w-full items-center gap-3 rounded-xl bg-eg-ombre px-4 py-3 text-left text-white hover:bg-eg-profond"
        >
          <Sparkles className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-medium">Répondre avec Claude, l'assistant général</span>
            <span className="block text-[12px] opacity-75">Votre demande lui est transmise telle quelle.</span>
          </span>
          <ArrowRight className="h-4 w-4 shrink-0" />
        </button>
      )}

      {suggestions.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.04em] text-eg-gris">
            Reprendre votre demande dans
          </p>
          {suggestions.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => ouvrirUseCase({ id: s.id, mode: 'natif' }, navigate)}
              className="flex w-full items-start gap-3 rounded-md border border-eg-bord
                bg-eg-blanc px-4 py-3 text-left transition-colors hover:border-eg-accent
                hover:bg-eg-papier"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-eg-encre">{s.titre}</span>
                  <span className={`shrink-0 rounded border px-1.5 py-px text-[10px]
                    ${TEINTES[s.pertinence] || TEINTES.faible}`}>
                    {s.pertinence}
                  </span>
                </div>
                <p className="mt-0.5 text-[12.5px] text-eg-gris">{s.raison}</p>
              </div>
              <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-eg-gris" />
            </button>
          ))}
          <p className="pt-1 text-[11px] text-eg-estompe">
            Votre demande n'est pas reportée automatiquement : recopiez-la dans le
            nouvel assistant.
          </p>
        </div>
      ) : (
        <p className="text-[12.5px] text-eg-gris">
          Aucun assistant ne correspond à cette demande pour le moment. Décrivez ce
          que vous cherchez depuis « Nouvelle conversation », la recherche vous
          orientera.
        </p>
      )}
    </div>
  );
};

export default Reorientation;
